import { useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { SBotBrandLogo } from "@/components/SBotBrandLogo";
import { ErrorText, PrimaryButton, SBCard, SegmentedControl, SportsBackground } from "@/components/ui";
import "./auth.css";

// Port of Views/Auth/AuthenticationView.swift. The keychain "save for Face ID
// switching" toggle is omitted — browsers handle credential storage natively.

type AuthMode = "signIn" | "createAccount";

export function AuthenticationView() {
  const { signIn, createAccount, signInWithGoogle, resetPassword, authError, isLoading } = useAuth();
  const [authMode, setAuthMode] = useState<AuthMode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const trimmedEmailValue = email.trim();
  const isSubmitDisabled =
    trimmedEmailValue.length === 0 ||
    password.trim().length === 0 ||
    (authMode === "createAccount" && (firstName.trim().length === 0 || lastName.trim().length === 0));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isSubmitDisabled || isLoading) return;
    if (authMode === "signIn") {
      void signIn(email, password);
    } else {
      void createAccount(email, password, firstName, lastName);
    }
  };

  return (
    <SportsBackground>
      <div className="auth-scroll">
        <SBCard className="auth-card">
          <header className="auth-header">
            <SBotBrandLogo className="auth-logo" />
            <h1>
              <span>SBot</span> Labs
            </h1>
            <p>Sports inference, built around your events, athletes, and questions.</p>
          </header>

          <form onSubmit={handleSubmit} className="auth-form">
            <button type="button" className="auth-google-button" onClick={() => void signInWithGoogle()} disabled={isLoading}>
              <span className="auth-google-badge" aria-hidden>
                G
              </span>
              Continue with Google
            </button>

            <div className="sb-divider-labeled">or</div>

            <SegmentedControl
              options={[
                { value: "signIn", label: "Sign In" },
                { value: "createAccount", label: "Create Account" },
              ]}
              value={authMode}
              onChange={setAuthMode}
            />

            {authMode === "createAccount" && (
              <div className="auth-name-row">
                <input
                  placeholder="First name"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <input
                  placeholder="Last name"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            )}

            <input
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              autoComplete={authMode === "signIn" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <PrimaryButton
              type="submit"
              title={authMode === "signIn" ? "Sign In" : "Create Account"}
              disabled={isSubmitDisabled || isLoading}
            />

            {authMode === "signIn" && (
              <button
                type="button"
                className="auth-reset-link"
                disabled={trimmedEmailValue.length === 0 || isLoading}
                onClick={() => void resetPassword(email)}
              >
                Reset password
              </button>
            )}

            <ErrorText>{authError}</ErrorText>
          </form>

          <footer className="auth-footer">SBot Sports Intelligence</footer>
        </SBCard>
      </div>
    </SportsBackground>
  );
}
